'use client';

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTeam } from '@/context/TeamContext';
import useEntitlements from '@/hooks/useEntitlements';
import { Button } from '@/components/button';
import { Heading } from '@/components/heading';
import { Text } from '@/components/text';
import { 
  SparklesIcon, 
  ShieldCheckIcon 
} from '@heroicons/react/24/outline';

export default function InviteProGuard({ children }) {
  const navigate = useNavigate();
  
  // 1. GET ALL DATA & LOADING STATES
  const { firebaseUser, profileLoading } = useAuth();
  const { teams, loadingTeams } = useTeam();
  const { isPro, loading: entitlementsLoading } = useEntitlements(firebaseUser?.uid);

  // 2. MASTER LOADING CHECK
  // We block the view if ANY of the critical data sources are still loading.
  // This prevents the "0 Teams" glitch where a user slips through before data arrives.
  const isInitializing = 
    (!!firebaseUser && profileLoading) || // Waiting for User Profile (Critical!)
    loadingTeams ||                       // Waiting for Teams List
    entitlementsLoading;                  // Waiting for Pro Check

  // 3. LOGIC GATE
  const canAcceptInvite = useMemo(() => {
    // If still loading, we assume "safe" momentarily, but the UI below blocks it via isInitializing
    if (isInitializing) return true; 
    
    // If not logged in, let them see the "Join" button (which redirects to Login)
    if (!firebaseUser) return true; 
    
    // Pro users have no limits
    if (isPro) return true; 

    // STRICT LIMIT CHECK: 
    // If Free Tier AND already managing 1+ teams -> BLOCK
    if (teams && teams.length >= 1) {
      return false;
    }

    return true;
  }, [isPro, teams, firebaseUser, isInitializing]);

  // 4. RENDER LOADING STATE
  if (isInitializing) {
    return (
      <div className="flex flex-col items-center justify-center p-12 space-y-4">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-blue-600" />
        <p className="text-xs text-zinc-400 font-medium">Verifying account...</p>
      </div>
    );
  }

  // 5. RENDER "LIMIT REACHED" BLOCKING SCREEN
  if (!canAcceptInvite) {
    return (
      <div className="mx-auto max-w-md px-6 py-12 text-center">
        
        {/* Visual Anchor */}
        <div className="mb-8 flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 animate-pulse rounded-full bg-blue-100 blur-xl dark:bg-blue-900/30"></div>
            <div className="relative flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-white to-zinc-50 shadow-xl ring-1 ring-zinc-900/5 dark:from-zinc-800 dark:to-zinc-900 dark:ring-white/10">
              <SparklesIcon className="h-10 w-10 text-blue-600" />
            </div>
            {/* Notification Badge */}
            <div className="absolute -right-2 -top-2 flex h-8 w-8 items-center justify-center rounded-full bg-zinc-900 text-xs font-bold text-white shadow ring-2 ring-white dark:ring-zinc-900">
              !
            </div>
          </div>
        </div>

        <Heading level={2} className="mb-3">
          Limit Reached
        </Heading>
        
        <Text className="mb-8 text-zinc-600 dark:text-zinc-400">
          You are currently managing <strong>{teams.length} team</strong> on the Free plan. 
          To join another team as staff, you'll need to upgrade your account.
        </Text>

        {/* Value Prop List */}
        <div className="mb-8 space-y-3 rounded-2xl bg-zinc-50 p-5 text-left dark:bg-zinc-800/50">
          <div className="flex items-start gap-3">
            <ShieldCheckIcon className="mt-0.5 h-5 w-5 text-blue-600" />
            <span className="text-sm text-zinc-700 dark:text-zinc-300">Manage unlimited teams</span>
          </div>
          <div className="flex items-start gap-3">
            <ShieldCheckIcon className="mt-0.5 h-5 w-5 text-blue-600" />
            <span className="text-sm text-zinc-700 dark:text-zinc-300">Unlock advanced match stats</span>
          </div>
        </div>

        {/* Action Area */}
        <div className="flex flex-col gap-3">
          <Button 
            className="w-full justify-center py-3 text-base shadow-blue-500/20 shadow-lg" 
            color="blue"
            onClick={() => navigate('/billing/upgrade')}
          >
            Upgrade to Pro
          </Button>
          
          <button 
            onClick={() => navigate('/dashboard')}
            className="w-full py-3 text-sm font-medium text-zinc-500 transition-colors hover:text-zinc-800 dark:hover:text-zinc-200"
          >
            No thanks, return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // 6. IF ALLOWED, RENDER CONTENT
  return <>{children}</>;
}